import React, { useCallback, useEffect, useState } from 'react';
import crmClient from './crmClient';
import { formatDate } from './format';

const UserAccessView = ({ meta }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('SALES');
  const [migrateResult, setMigrateResult] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [classifyResult, setClassifyResult] = useState(null);
  const [classifying, setClassifying] = useState(false);
  const [seqResult, setSeqResult] = useState(null);
  const [runningSeq, setRunningSeq] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    crmClient
      .listUsers()
      .then(payload => {
        setUsers((payload && payload.users) || []);
        setLoading(false);
      })
      .catch(() => {
        setError('Unable to load CRM users.');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = (action, after) => {
    setBusy(true);
    setError(null);
    action()
      .then(() => {
        setBusy(false);
        if (after) after();
        load();
      })
      .catch(err => {
        setBusy(false);
        setError((err && err.response && err.response.data && err.response.data.message) || 'Action failed.');
      });
  };

  const addUser = () => {
    if (!newEmail.trim()) return;
    run(() => crmClient.addUser({ email: newEmail.trim(), crm_role: newRole }), () => setNewEmail(''));
  };

  const runMigration = () => {
    setMigrating(true);
    setError(null);
    crmClient
      .migrateMerchants()
      .then(payload => {
        setMigrating(false);
        setMigrateResult((payload && payload.summary) || null);
      })
      .catch(err => {
        setMigrating(false);
        setError((err && err.response && err.response.data && err.response.data.message) || 'Migration failed.');
      });
  };

  const runClassification = () => {
    setClassifying(true);
    setError(null);
    crmClient
      .runClassification({})
      .then(payload => {
        setClassifying(false);
        setClassifyResult((payload && payload.summary) || null);
      })
      .catch(err => {
        setClassifying(false);
        setError((err && err.response && err.response.data && err.response.data.message) || 'Classification failed.');
      });
  };

  const runSequences = () => {
    setRunningSeq(true);
    setError(null);
    crmClient
      .runSequences()
      .then(payload => {
        setRunningSeq(false);
        setSeqResult((payload && payload.summary) || null);
      })
      .catch(err => {
        setRunningSeq(false);
        setError((err && err.response && err.response.data && err.response.data.message) || 'Sequence run failed.');
      });
  };

  return (
    <div className="crm-settings">
      <section className="crm-card">
        <h3>Grant CRM access</h3>
        <p className="crm-muted">Grant an existing Gophr dashboard user access to the CRM. They keep their normal Gophr login.</p>
        <div className="crm-field-row">
          <label className="crm-field-grow">
            <span>Dashboard user email</span>
            <input type="email" value={newEmail} placeholder="name@gophr.app" onChange={event => setNewEmail(event.target.value)} />
          </label>
          <label>
            <span>Role</span>
            <select value={newRole} onChange={event => setNewRole(event.target.value)}>
              <option value="SALES">CRM Sales User</option>
              <option value="ADMIN">CRM Admin</option>
            </select>
          </label>
          <button type="button" className="crm-btn" disabled={busy || !newEmail.trim()} onClick={addUser}>
            Add CRM user
          </button>
        </div>
      </section>

      {error && <div className="crm-inline-error">{error}</div>}

      <section className="crm-card">
        <h3>CRM users</h3>
        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Auth0 account</th>
                <th>Role</th>
                <th>Access</th>
                <th>Added</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="crm-empty">
                    Loading users…
                  </td>
                </tr>
              )}
              {!loading &&
                users.map(user => (
                  <tr key={user.crm_user_id}>
                    <td>
                      <strong>{user.email || '—'}</strong>
                    </td>
                    <td>
                      <small>{user.dashboard_user_auth0_id || 'links on first login'}</small>
                    </td>
                    <td>
                      <select
                        value={user.crm_role}
                        disabled={busy}
                        onChange={event => run(() => crmClient.updateUser(user.crm_user_id, { crm_role: event.target.value }))}
                      >
                        <option value="SALES">Sales</option>
                        <option value="ADMIN">Admin</option>
                      </select>
                    </td>
                    <td>
                      <span className={`crm-access-dot${user.crm_access ? ' crm-access-dot--on' : ''}`} />
                      {user.crm_access ? 'Enabled' : 'Revoked'}
                    </td>
                    <td>{formatDate(user.created_at)}</td>
                    <td className="crm-num">
                      <button
                        type="button"
                        className="crm-btn crm-btn--danger crm-btn--sm"
                        disabled={busy}
                        onClick={() => run(() => crmClient.removeUser(user.crm_user_id))}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="crm-card">
        <h3>Existing merchant migration</h3>
        <p className="crm-muted">
          Classify every existing Gophr merchant into their correct lifecycle stage from live order history. Safe to run repeatedly.
        </p>
        <button type="button" className="crm-btn" disabled={migrating} onClick={runMigration}>
          {migrating ? 'Running migration…' : 'Run merchant migration'}
        </button>
        {migrateResult && (
          <div className="crm-migrate-summary">
            <span>Stores processed: <strong>{migrateResult.total_stores}</strong></span>
            <span>Created: <strong>{migrateResult.created}</strong></span>
            <span>Updated: <strong>{migrateResult.updated}</strong></span>
            <span>Stage changes: <strong>{migrateResult.stage_changes}</strong></span>
            <span>Errors: <strong>{migrateResult.errors}</strong></span>
          </div>
        )}
      </section>

      <section className="crm-card">
        <h3>AI classification</h3>
        <p className="crm-muted">
          Classify every account by business category and subcategory from its name, domain, and notes. Human-set categories are preserved.
        </p>
        <button type="button" className="crm-btn" disabled={classifying} onClick={runClassification}>
          {classifying ? 'Classifying…' : 'Run AI classification'}
        </button>
        {classifyResult && (
          <div className="crm-migrate-summary">
            <span>Evaluated: <strong>{classifyResult.evaluated}</strong></span>
            <span>Classified: <strong>{classifyResult.classified}</strong></span>
            <span>Skipped (human): <strong>{classifyResult.skipped}</strong></span>
          </div>
        )}
      </section>

      <section className="crm-card">
        <h3>Automated email sequences</h3>
        <p className="crm-muted">
          Send due onboarding, day-3 follow-up, re-engagement, and win-back emails. Provider:{' '}
          <strong>{(meta && meta.email_provider && meta.email_provider.provider) || 'log'}</strong>
          {meta && meta.email_provider && !meta.email_provider.configured ? ' (not configured — running in log mode)' : ''}.
        </p>
        <button type="button" className="crm-btn" disabled={runningSeq} onClick={runSequences}>
          {runningSeq ? 'Running sequences…' : 'Run email sequences now'}
        </button>
        {seqResult && (
          <div className="crm-migrate-summary">
            {Object.keys(seqResult).map(key => (
              <span key={key}>
                {key}: <strong>{seqResult[key].sent} sent</strong>
              </span>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default UserAccessView;
